import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';

const SHAPE_GENERATORS = [
  // Sine wave
  (canvas, padding) => {
    const path = [];
    const segments = 30;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = padding + (canvas.width - 2 * padding) * t;
      const y = canvas.height / 2 + Math.sin(t * Math.PI * 3) * 120;
      path.push({ x, y });
    }
    return { path, name: 'Wave' };
  },
  // Spiral
  (canvas, padding) => {
    const path = [];
    const segments = 60;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(20, Math.min(canvas.width, canvas.height) / 2 - padding);
    const outerRadius = Math.min(200, maxRadius);
    const innerRadius = outerRadius * 0.25;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * Math.PI * 4;
      const radius = innerRadius + t * (outerRadius - innerRadius);
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      path.push({ x, y });
    }
    return { path, name: 'Spiral' };
  },
  // Heart
  (canvas, padding) => {
    const path = [];
    const segments = 60;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(20, Math.min(canvas.width, canvas.height) / 2 - padding);
    const scale = 8 * Math.min(1, maxRadius / 150);
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2;
      const x = centerX + scale * 16 * Math.pow(Math.sin(t), 3);
      const y = centerY - scale * (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
      path.push({ x, y });
    }
    return { path, name: 'Heart' };
  },
  // Figure 8 / Infinity
  (canvas, padding) => {
    const path = [];
    const segments = 60;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(20, Math.min(canvas.width, canvas.height) / 2 - padding);
    const scale = Math.min(120, maxRadius);
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2;
      const x = centerX + scale * Math.sin(t);
      const y = centerY + scale * Math.sin(t) * Math.cos(t);
      path.push({ x, y });
    }
    return { path, name: 'Infinity' };
  },
  // Star
  (canvas, padding) => {
    const path = [];
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(20, Math.min(canvas.width, canvas.height) / 2 - padding);
    const outerRadius = Math.min(150, maxRadius);
    const innerRadius = outerRadius * 0.4;
    const points = 5;
    for (let i = 0; i <= points * 2; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      path.push({ x, y });
    }
    // Close the star
    path.push(path[0]);
    return { path, name: 'Star' };
  },
  // Circle
  (canvas, padding) => {
    const path = [];
    const segments = 40;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const maxRadius = Math.max(20, Math.min(canvas.width, canvas.height) / 2 - padding);
    const radius = Math.min(150, maxRadius);
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2;
      const x = centerX + Math.cos(t) * radius;
      const y = centerY + Math.sin(t) * radius;
      path.push({ x, y });
    }
    return { path, name: 'Circle' };
  },
  // Zigzag
  (canvas, padding) => {
    const path = [];
    const segments = 10;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const x = padding + (canvas.width - 2 * padding) * t;
      const y = canvas.height / 2 + (i % 2 === 0 ? -100 : 100);
      path.push({ x, y });
    }
    return { path, name: 'Zigzag' };
  },
  // Mountain range
  (canvas, padding) => {
    const path = [];
    const peaks = 4;
    const segmentsPerPeak = 10;
    for (let p = 0; p < peaks; p++) {
      for (let i = 0; i <= segmentsPerPeak; i++) {
        const t = (p * segmentsPerPeak + i) / (peaks * segmentsPerPeak);
        const x = padding + (canvas.width - 2 * padding) * t;
        const peakT = i / segmentsPerPeak;
        const height = Math.sin(peakT * Math.PI) * (80 + Math.random() * 40);
        const y = canvas.height / 2 + 50 - height;
        path.push({ x, y });
      }
    }
    return { path, name: 'Mountains' };
  }
];

const NeonTracer = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [shapeName, setShapeName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const pathRef = useRef([]);
  const userPathRef = useRef([]);
  const shapeIndexRef = useRef(0);

  const generatePath = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const padding = 80;
    
    // Get next shape (cycling through all shapes)
    const generator = SHAPE_GENERATORS[shapeIndexRef.current];
    const { path, name } = generator(canvas, padding);
    
    pathRef.current = path;
    setShapeName(name);
  }, []);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Dark background
    ctx.fillStyle = '#0a0a1e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Grid effect
    ctx.strokeStyle = 'rgba(57, 255, 20, 0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < canvas.width; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Draw target path (glowing guide)
    if (pathRef.current.length > 0) {
      ctx.strokeStyle = '#39FF14';
      ctx.lineWidth = 25;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowBlur = 30;
      ctx.shadowColor = '#39FF14';
      ctx.globalAlpha = 0.25;
      
      ctx.beginPath();
      pathRef.current.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
      
      // Draw start and end markers
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#39FF14';
      ctx.beginPath();
      ctx.arc(pathRef.current[0].x, pathRef.current[0].y, 15, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#FF1493';
      ctx.beginPath();
      ctx.arc(pathRef.current[pathRef.current.length - 1].x, pathRef.current[pathRef.current.length - 1].y, 15, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw user path
    if (userPathRef.current.length > 1) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#00FFFF';
      ctx.lineWidth = 8;
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#00FFFF';
      
      ctx.beginPath();
      userPathRef.current.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    }
    
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const handleResize = () => {
      canvas.width = Math.min(window.innerWidth, 1200);
      canvas.height = Math.min(window.innerHeight - 100, 700);
      // The canvas coordinate space just changed, so any in-progress stroke
      // (recorded in the old space) is no longer valid — abort it.
      userPathRef.current = [];
      setIsDrawing(false);
      generatePath();
      drawCanvas();
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [generatePath, drawCanvas]);

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPointerPos(e);
    userPathRef.current = [pos];
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    userPathRef.current.push(pos);
    drawCanvas();
  };

  const handlePointerUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    calculateScore();
  };

  const calculateScore = () => {
    if (userPathRef.current.length < 2 || pathRef.current.length < 2) {
      setScore(0);
      return;
    }
    
    let totalAccuracy = 0;
    const checkPoints = 50;
    
    for (let i = 0; i < checkPoints; i++) {
      const t = i / (checkPoints - 1);

      // Get point on target path (the target path has a fixed, parametrically
      // uniform point count per shape, so proportional indexing is fine here)
      const targetIndex = Math.min(
        Math.floor(t * (pathRef.current.length - 1)),
        pathRef.current.length - 1
      );
      const targetPoint = pathRef.current[targetIndex];

      // Find the closest point anywhere on the user's drawn path, rather than
      // assuming the same index-proportion — the user path's point density
      // varies with drawing speed, not position along the curve, so matching
      // by index would compare mismatched locations.
      let closestDistance = Infinity;
      for (let j = 0; j < userPathRef.current.length; j++) {
        const candidate = userPathRef.current[j];
        const d = Math.sqrt(
          Math.pow(targetPoint.x - candidate.x, 2) +
          Math.pow(targetPoint.y - candidate.y, 2)
        );
        if (d < closestDistance) closestDistance = d;
      }

      if (Number.isFinite(closestDistance)) {
        // Score based on distance (closer = higher score)
        const pointScore = Math.max(0, 1 - closestDistance / 100);
        totalAccuracy += pointScore;
      }
    }
    
    setScore(Math.round((totalAccuracy / checkPoints) * 100));
  };

  const reset = () => {
    userPathRef.current = [];
    setScore(0);
    // Move to next shape
    shapeIndexRef.current = (shapeIndexRef.current + 1) % SHAPE_GENERATORS.length;
    generatePath();
    drawCanvas();
  };

  return (
    <div className="min-h-screen bg-[#0a0a1e] relative overflow-hidden flex flex-col">
      <div className="relative z-10 p-4 md:p-6">
        <header className="flex justify-between items-center">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          
          <div className="glass-effect px-6 md:px-8 py-3 md:py-4 rounded-2xl text-center">
            <p className="text-white/70 text-xs mb-1">{shapeName}</p>
            <p className="text-white">
              <span className="text-sm">Accuracy</span>
              <span className="block text-3xl md:text-4xl font-bold text-[#39FF14]">{score}%</span>
            </p>
          </div>
          
          <button
            data-testid="reset-tracer"
            onClick={reset}
            className="glass-effect text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            <span className="hidden sm:inline">New Shape</span>
          </button>
        </header>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="glass-effect rounded-3xl p-2 md:p-4">
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            className="rounded-2xl cursor-crosshair"
            style={{ touchAction: 'none' }}
          />
        </div>
      </div>

      <div className="relative z-10 p-4 text-center">
        <div className="glass-effect inline-block px-6 py-3 rounded-2xl text-white">
          <p>🟢 Start at green dot → Trace the path → 🩷 End at pink dot</p>
          <p className="text-sm text-white/70 mt-1">Press "New Shape" to try a different pattern!</p>
        </div>
      </div>
    </div>
  );
};

export default NeonTracer;
